import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { AppShell } from "@/components/layout/app-shell";
import { 
  Table, 
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow 
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { PlusCircle, Search, MoreHorizontal, Eye, Trash2, Clock, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useCustomer } from "@/hooks/use-customer";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { formatDate } from "@/lib/utils";
import { Helmet } from 'react-helmet';

export default function SavedAnalysesPage() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState<number | null>(null);
  const { activeCustomerId, setActiveCustomerId } = useCustomer();

  const queryKey = activeCustomerId ? `/api/analyses?customerId=${activeCustomerId}` : "/api/analyses?all=true";

  // Fetch all analyses
  const { data: analyses = [] as any[], isLoading } = useQuery<any[]>({
    queryKey: [queryKey],
  });

  // Filter analyses based on search query and status
  const filteredAnalyses = analyses.filter((analysis: any) => {
    const matchesSearch = 
      searchQuery === "" || 
      analysis.manufacturer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      analysis.model.toLowerCase().includes(searchQuery.toLowerCase()) ||
      analysis.location.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || analysis.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Handle delete analysis
  const deleteAnalysis = async (id: number) => {
    try {
      await apiRequest("DELETE", `/api/analyses/${id}`);
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast({
        title: t("savedAnalyses.deleteSuccess"),
        description: t("savedAnalyses.deleteSuccessDesc"),
      });
    } catch (error) {
      toast({
        title: t("savedAnalyses.deleteError"),
        description: (error as Error).message,
        variant: "destructive",
      });
    }
  };

  // Open delete confirmation dialog
  const openDeleteConfirm = (id: number) => {
    setSelectedAnalysisId(id);
    setDeleteConfirmOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (selectedAnalysisId) {
      deleteAnalysis(selectedAnalysisId);
    }
    setDeleteConfirmOpen(false);
  };

  return (
    <AppShell>
      <Helmet>
        <title>{t("savedAnalyses.pageTitle")} | Pump ECM</title>
        <meta name="description" content={t("savedAnalyses.pageDescription")} />
      </Helmet>

      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">{t("savedAnalyses.title")}</h1>
          <p className="text-muted-foreground">{t("savedAnalyses.subtitle")}</p>
        </div>
        <Button 
          onClick={() => navigate("/analysis/new")}
          className="flex items-center gap-2"
        >
          <PlusCircle className="h-4 w-4" />
          {t("savedAnalyses.newAnalysis")}
        </Button>
      </div>

      <Card className="mb-8">
        <CardHeader className="pb-3">
          <CardTitle>{t("savedAnalyses.yourAnalyses")}</CardTitle>
          <CardDescription>{t("savedAnalyses.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
            <div className="relative w-full md:w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("savedAnalyses.search")}
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder={t("savedAnalyses.filterStatus")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("savedAnalyses.allStatuses")}</SelectItem>
                <SelectItem value="draft">{t("savedAnalyses.draftStatus")}</SelectItem>
                <SelectItem value="submitted">{t("savedAnalyses.submittedStatus")}</SelectItem>
                <SelectItem value="completed">{t("savedAnalyses.completedStatus")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredAnalyses.length > 0 ? (
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("savedAnalyses.tableHeader.manufacturer")}</TableHead>
                    <TableHead>{t("savedAnalyses.tableHeader.model")}</TableHead>
                    <TableHead>{t("savedAnalyses.tableHeader.location")}</TableHead>
                    {activeCustomerId === null && <TableHead>Facility</TableHead>}
                    <TableHead>{t("savedAnalyses.tableHeader.date")}</TableHead>
                    <TableHead>{t("savedAnalyses.tableHeader.status")}</TableHead>
                    <TableHead className="text-right">{t("savedAnalyses.tableHeader.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAnalyses.map((analysis: any) => (
                    <TableRow key={analysis.id}>
                      <TableCell className="font-medium">{analysis.manufacturer}</TableCell>
                      <TableCell>{analysis.model}</TableCell>
                      <TableCell>{analysis.location}</TableCell>
                      {activeCustomerId === null && (
                        <TableCell>
                          <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                            {analysis.buildingName || "Unknown"}
                          </span>
                        </TableCell>
                      )}
                      <TableCell>{formatDate(new Date(analysis.createdAt))}</TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          {analysis.status === 'submitted' ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin text-blue-500" />
                          ) : analysis.status === 'completed' ? (
                            <div className="h-2 w-2 rounded-full bg-green-500 mr-2" />
                          ) : (
                            <Clock className="h-4 w-4 mr-2 text-orange-500" />
                          )}
                          {t(`savedAnalyses.${analysis.status}Status`)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <span className="sr-only">{t("savedAnalyses.openMenu")}</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              if (analysis.buildingId) {
                                setActiveCustomerId(analysis.buildingId);
                              }
                              navigate(`/analysis/${analysis.id}`);
                            }}>
                              <Eye className="mr-2 h-4 w-4" />
                              {analysis.status === 'completed' 
                                ? t("savedAnalyses.viewResults") 
                                : t("savedAnalyses.viewDetails")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openDeleteConfirm(analysis.id)}>
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t("savedAnalyses.delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-10 border rounded-lg bg-muted/30">
              <p className="text-muted-foreground mb-4">
                {searchQuery || statusFilter !== "all" 
                  ? t("savedAnalyses.noResults") 
                  : t("savedAnalyses.noAnalyses")}
              </p>
              {searchQuery || statusFilter !== "all" ? (
                <Button 
                  variant="outline"
                  onClick={() => {
                    setSearchQuery("");
                    setStatusFilter("all");
                  }}
                >
                  {t("savedAnalyses.clearFilters")}
                </Button>
              ) : (
                <Button 
                  variant="outline"
                  onClick={() => navigate("/analysis/new")}
                >
                  {t("savedAnalyses.createFirst")}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("savedAnalyses.deleteConfirm.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("savedAnalyses.deleteConfirm.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
